(ns minorodata.components.transformations.utils
  (:require
   ["lucide-react" :refer [ChevronRight FileEdit MoreHorizontal PlusCircle]]
   [minorodata.app.handlers :refer [dispatch dispatch-sync]]
   [minorodata.app.utils :refer [assoc-some]]
   [re-posh.core :as re-posh]
   [ui.dropdown-menu :refer [dropdown-menu-content dropdown-menu-item
                             dropdown-menu-root dropdown-menu-separator
                             dropdown-menu-sub-content dropdown-menu-sub-root
                             dropdown-menu-sub-trigger dropdown-menu-trigger]]
   [ui.utils :refer [clsx]]))

(defn create-and-edit
  "Tries to create a transform. If no transform provided, it will try getting code from localstorage. Localstorage is used to send code to sql console from other parts of the app, such STX pages."
  [group-id & [{:keys [replace-tab-idx transform]}]]
  (let [command @(re-posh/subscribe [:group.subs/new-transform-template-code])
        open-modal (fn [t]
                     (js/setTimeout (fn []
                                      (dispatch [:group.events/clean-up-new-transform-template-code])
                                      (dispatch-sync [:sql-console-modal.events/open-transform (:transform/id t) {:replace-tab-idx replace-tab-idx}]))
                                    10))]
    (dispatch [:environments.api/create-transform {:transform (or transform
                                                                  {:command command})
                                                   :group-id   group-id
                                                   :on-success open-modal}])))

(defn show-edit-transform-sql-modal [{:keys [id]}]
  (dispatch [:sql-console-modal.events/open-transform id]))

(defn transform-edit-sql-button [{:keys [transform disabled size on-click]}]
  [:div {:title    "Edit SQL"
         :data-cy  "transform-edit-btn"
         :on-click #(do
                      (show-edit-transform-sql-modal transform)
                      (when on-click (on-click)))}
   [:> FileEdit (cond->
                 {:class (clsx "cursor-pointer hover:text-kleene-secondary-v2 transition-colors"
                               {"text-kleene-text-secondary!" disabled})}
                  size (assoc :size size))]])

(defn show-webhooks-modal [{:keys [master-id group-id]}]
  (dispatch [:app.events/navigate-query {:webhooks     true
                                         :master-id    master-id
                                         :group-id     group-id}]))


(defn show-notifications-modal [{:keys [master-id group-id]}]
  (dispatch [:app.events/navigate-query {:notifications true
                                         :master-id     master-id
                                         :group-id      group-id}]))


(defn show-transform-delete-modal [{:keys [id group-id pipeline-editor]}]
  (dispatch [:app.events/navigate-query (assoc-some {:delete-transform true
                                                     :transform-id     id
                                                     :group-id         group-id}
                                                    :pipeline-editor pipeline-editor)]))

(defn show-transform-settings-modal [{:keys [transform-id group-id]}]
  (dispatch [:app.events/navigate-query {:transform-settings true
                                         :transform-id        transform-id
                                         :group-id            group-id}]))

(defn go-to-dag [transform]
  (let [{:keys [master-id]} transform]
    (dispatch [:app.events/navigate :dag nil {:focus master-id}])))


(defn go-to-logs [{:keys [name]}]
  (dispatch [:app.events/navigate :log {} {:source "job-manager"
                                           :term   name}]))

(defn go-to-dynamic-docs [{:keys [master-id]}]
  (dispatch [:app.events/navigate :dynamic-docs-details {:entity-id master-id}]))

(defn show-versions-history [transform & [on-rollback-ev]]
  (dispatch [:transform.events/toggle-version-select-modal true transform {:on-rollback-ev on-rollback-ev}]))

(defn move-transform [transform-id group-id & [on-success]]
  (dispatch [:transform.api/move-to-group {:to-group     group-id
                                           :transform-id transform-id
                                           :on-success   on-success
                                           :redirect?    false}]))
(defn clone-transform [transform]
  (dispatch [:app.events/navigate-query {:clone-transform    true
                                         :clone-transform-id (:id transform)
                                         :clone-group-id     (:group-id transform)}]))

(defn ask-ai [transform]
  (dispatch [:ai-chat/set-context transform])
  (dispatch [:modals.events/toggle-modal "ai-modal" true]))

(defn move-transform-submenu [{:keys [on-move on-new-group title disabled]}]
  (let [groups    @(re-posh/subscribe [:environments.subs/get-groups])
        prod-env? @(re-posh/subscribe [:environments.subs/production-active?])]
    [dropdown-menu-sub-root
     [dropdown-menu-sub-trigger {:data-cy  "transform-more-move-btn"
                                 :disabled (or disabled (not prod-env?))}
      [:span {:class "flex items-center gap-2 justify-between w-full"}
       [:span title]
       [:> ChevronRight {:size 16}]]]
     [dropdown-menu-sub-content
      [dropdown-menu-item {:data-cy  "transform-more-move-option-btn transform-more-move-option-create-group"
                           :on-click on-new-group}
       [:> PlusCircle {:class "mr-2" :size 20}]
       "Move to a new group"]
      [dropdown-menu-separator {:class "my-1"}]
      (for [{:group/keys [id name]} groups]
        ^{:key id}
        [dropdown-menu-item {:data-cy  (str "transform-more-move-option-btn transform-more-move-option-" id)
                             :on-click #(on-move id)}
         name])]]))

(defn more-options [transform-id & [excluded]]
  (filter
   #(and %
         (or (not excluded)
             (not (excluded (:id %)))))
   [{:id       :settings
     :title    "Settings"
     :data-cy  "transform-more-settings-btn"
     :on-click show-transform-settings-modal}
    {:id         :show-history
     :only-prod? true
     :title      "Show history"
     :data-cy    "transform-more-history-btn"
     :on-click   show-versions-history}
    {:id         :show-in-dag
     :only-prod? true
     :title      "Show in Pipelines"
     :data-cy    "transform-more-show-in-dag-btn"
     :on-click   go-to-dag}
    {:id         :show-in-dynamic-docs
     :only-prod? true
     :title      "Show in Data docs"
     :data-cy    "transform-more-show-in-docs-btn"
     :on-click   go-to-dynamic-docs}
    {:id         :show-in-logs
     :only-prod? true
     :title      "Show in Logs"
     :data-cy    "transform-more-show-in-logs-btn"
     :on-click   go-to-logs}
    {:id         :show-in-pipeline-editor
     :only-prod? true
     :title      "Show in Pipeline Editor"
     :data-cy    "transform-more-show-in-pipeline-editor-btn"
     :on-click   (fn [transform]
                   (dispatch [:pipeline-editor.events/navigate-and-open-tab
                              {:id       (:master-id transform)
                               :group-id (:group-id transform)
                               :type     "transform"
                               :name     (:name transform)}]))}

    (when @(re-posh/subscribe [:unleash/is-enabled "fe.ai-chat"])
      {:id         :ask-ai
       :only-prod? true
       :title      "Ask AI"
       :data-cy    "transform-ask-ai-btn"
       :on-click   ask-ai})


    {:id         :move-transform
     :submenu    [move-transform-submenu {:title        "Move transform"
                                          :on-move      #(move-transform transform-id % (fn [] (dispatch [:transform.events/refresh-panel])))
                                          :on-new-group #(dispatch [:app.events/navigate-query
                                                                    {:create-group   true
                                                                     :move-transform transform-id}])}]}
    {:id         :clone
     :title      "Clone transform"
     :data-cy    "transform-more-clone-btn"
     :on-click   clone-transform}
    (when @(re-posh/subscribe [:unleash/is-enabled "fe.webhooks"])
      {:id         :webhooks
       :title      "Webhooks"
       :data-cy    "transform-more-webhooks-btn"
       :on-click   show-webhooks-modal})
    (when @(re-posh/subscribe [:unleash/is-enabled "fe.notifications"])
      {:id         :notifications
       :title      "Notifications"
       :data-cy    "transform-more-notifications-btn"
       :on-click   show-notifications-modal})
    {:id       :delete
     :title    "Delete"
     :class    "text-kleene-error-01"
     :data-cy  "transform-more-delete-btn"
     :on-click show-transform-delete-modal}]))

(defn transform-more-options-button [{:keys [transform size disabled on-option-click exclude-options]}]
  (let [prod-env?    @(re-posh/subscribe [:environments.subs/production-active?])
        transform-id (:id transform)]
    [dropdown-menu-root
     [dropdown-menu-trigger {:disabled disabled
                             :data-cy  "transform-more-btn"
                             :title    "More options"}
      [:> MoreHorizontal (cond->
                          {:class (clsx "cursor-pointer hover:text-kleene-secondary-v2 transition-colors"
                                        {"text-kleene-text-secondary!" disabled})}
                           size (assoc :size size))]]
     (into [dropdown-menu-content]
           (for [{:keys [title on-click only-prod? class submenu data-cy]} (more-options transform-id exclude-options)]
             (if-not submenu
               [dropdown-menu-item {:on-click #(do
                                                 (on-click transform)
                                                 (when on-option-click (on-option-click)))
                                    :class    class
                                    :data-cy  data-cy
                                    :disabled (when only-prod?
                                                (not prod-env?))}
                title]

               submenu)))]))

